/**
 * Whether a campaign is reaching anyone, and which one is on screen.
 *
 * Pure and separate from the list component because this is the part that is
 * easy to get wrong: the schedule is stored in Riyadh wall-clock time with no
 * timezone on it, and read in a browser that may be anywhere.
 */

// Saudi Arabia is UTC+3 year-round with no daylight saving, so one constant is
// enough — the same assumption app/utils/timezone.py makes on the backend.
export const RIYADH_OFFSET_MS = 3 * 60 * 60 * 1000;

/**
 * Parse a stored schedule value into something comparable with `storeNow()`.
 *
 * Both sides come out as instants whose UTC fields spell the Riyadh clock, so
 * comparing them compares wall clock against wall clock. Reading the value as
 * browser-local time instead would put an admin in another timezone at odds
 * with what the storefront actually does.
 */
export const parseStoreWallClock = (raw) => {
  if (!raw) return null;
  // Tolerate a value that already carries a zone: drop it and re-anchor, so a
  // future backend change cannot turn this into an Invalid Date comparison
  // that silently reports every campaign as live.
  const naive = String(raw).replace(/(Z|[+-]\d{2}:?\d{2})$/, "");
  const parsed = new Date(`${naive}Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const storeNow = (nowMs = Date.now()) => new Date(nowMs + RIYADH_OFFSET_MS);

/**
 * The four states a campaign can be in.
 *
 * "Scheduled" is deliberately not a shade of active: a popup that is switched
 * on but whose window has not opened is showing to nobody, and reading it as
 * live is exactly the confusion this column exists to remove.
 */
export const liveState = (popup, nowMs = Date.now()) => {
  if (!popup.is_active) return { label: "Off", tone: "inactive" };
  const now = storeNow(nowMs);
  const startsAt = parseStoreWallClock(popup.starts_at);
  const endsAt = parseStoreWallClock(popup.ends_at);
  if (startsAt && startsAt > now) return { label: "Scheduled", tone: "scheduled" };
  if (endsAt && endsAt < now) return { label: "Finished", tone: "inactive" };
  return { label: "Live", tone: "active" };
};

/**
 * Whether this view of the list is complete enough to name a winner.
 *
 * Under a filter, or deeper in the pagination, a higher-ranked campaign may
 * simply not be on screen — naming a winner from an incomplete list would be
 * a confident guess.
 */
export const canRankPage = (params) =>
  params.page === 1 &&
  !params.search_key &&
  !params.audience &&
  !params.surface &&
  !params.is_active;

/**
 * Which campaign is actually on screen right now, or null if this view cannot
 * tell.
 *
 * Only one popup shows at a time — the storefront takes the eligible row with
 * the lowest position, and the list arrives in that same order. So the first
 * live row wins and every live row after it is queued behind it, which is
 * worth saying plainly: "my campaign is switched on but nobody sees it" is
 * otherwise a support question with no visible answer.
 */
export const findShowingNowId = (popups, params, nowMs = Date.now()) => {
  if (!canRankPage(params)) return null;
  const winner = popups.find((popup) => liveState(popup, nowMs).label === "Live");
  return winner ? winner.id : null;
};

export const QUEUED_STATE = {
  label: "Queued",
  tone: "scheduled",
  title:
    "Switched on, but another campaign is ahead of it. Only one popup shows at a time.",
};

/** The state to draw for one row, given who is currently winning. */
export const rowState = (popup, showingNowId, nowMs = Date.now()) => {
  const base = liveState(popup, nowMs);
  const isQueued =
    base.label === "Live" && showingNowId !== null && popup.id !== showingNowId;
  return isQueued ? QUEUED_STATE : base;
};
